import {
  PLACEHOLDER_DECIMAL,
  PLACEHOLDER_POSITIVE_INTEGER,
  PLACEHOLDER_TAX,
  currencyToCountry,
  getCurrencySymbol,
  validCurrencies,
} from "@/components/ui/utils";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";

interface WholeNumberInputProps {
  className: string;
  value?: number;
  setValue: (value?: number) => void;
  validator?: boolean;
  onBlur?: () => void;

  disabled?: boolean;
  currency?: string;
}

function allowForSelectionAndDeletion(event: React.KeyboardEvent<HTMLInputElement>): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.key === "Backspace" ||
    event.key === "ArrowLeft" ||
    event.key === "ArrowRight" ||
    event.key === "Delete" ||
    event.key === "Tab"
  );
}

// input field that accepts only positive integers
// value is undefined if nothing has been typed in
export const WholeNumberInput = ({
  className,
  value,
  setValue,
  validator,
  onBlur,
  disabled,
}: WholeNumberInputProps) => {
  const [didUserInputZero, setDidUserInputZero] = useState(value === 0);
  return (
    <input
      type="number"
      className={className + " " + (validator ? "" : "border-red-500 bg-red-50")}
      disabled={disabled === undefined ? false : disabled}
      onClick={(e) => e.currentTarget.select()}
      placeholder={PLACEHOLDER_POSITIVE_INTEGER}
      value={value === 0 && !didUserInputZero ? "" : value}
      onBlur={onBlur}
      onKeyDown={(event) => {
        // both keydown and on change checks are required
        // allow for selection and deletion
        if (allowForSelectionAndDeletion(event)) {
          return;
        }

        // permit only integers
        if (!/[0-9]/.test(event.key)) {
          event.preventDefault();
        }
      }}
      onWheel={(e) => (e.target as HTMLInputElement).blur()}
      onKeyUp={onBlur ? (e) => (e.key === "Enter" ? onBlur() : undefined) : undefined}
      onChange={(e) => {
        // permit only integers, for button press
        if (e.target.value.replaceAll(/[0-9]*/g, "").length !== 0) {
          e.preventDefault();
          return;
        }

        const newValue = e.target.value.replace("/\D/", "");

        if (newValue === "") {
          setValue(undefined);
          setDidUserInputZero(false);
        } else {
          const parsedValue = parseInt(newValue);

          if (parsedValue === 0) {
            setValue(parsedValue);
            setDidUserInputZero(true);
          } else {
            setValue(parsedValue);
          }
        }
      }}
    />
  );
};

interface DecimalInputProps {
  className: string;
  value?: number;
  setValue: (value?: number) => void;
  validator: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  currency?: string;
}
// input field that accepts only positive decimals (7% -> 0.07 in value)
// value is undefined if nothing has been typed in
interface DecimalInputProps {
  className: string;
  value?: number;
  setValue: (value?: number) => void;
  validator: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
}

export const DecimalInput = ({
  className,
  value,
  setValue,
  validator,
  onBlur,
  disabled,
  placeholder,
  currency,
}: DecimalInputProps) => {
  const [didUserInputZero, setDidUserInputZero] = useState(value === 0);
  const currencySymbol = currency ? getCurrencySymbol(currency) : "";

  return (
    <div className="relative flex items-center">
      {currencySymbol && <span className="absolute left-3 text-gray-500 pointer-events-none">{currencySymbol}</span>}
      <input
        type="number"
        className={className + " flex-1 pl-8 " + (validator ? "" : "border-red-500 bg-red-50")}
        onClick={(e) => e.currentTarget.select()}
        placeholder={placeholder || PLACEHOLDER_DECIMAL}
        disabled={disabled === undefined ? false : disabled}
        value={value === 0 && !didUserInputZero ? "" : value}
        onBlur={onBlur}
        onKeyDown={(event) => {
          // allow for selection and deletion
          if (allowForSelectionAndDeletion(event)) {
            return;
          }

          // permit only positive decimals
          if (!/[0-9.]/.test(event.key)) {
            event.preventDefault();
          }
        }}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        onKeyUp={onBlur ? (e) => (e.key === "Enter" ? onBlur() : undefined) : undefined}
        onChange={(e) => {
          // permit only decimals, for button press
          if (e.target.value.replaceAll(/[0-9.]*/g, "").length !== 0) {
            console.log("PREVENT");
            e.preventDefault();
            return;
          }

          const newValue = e.target.value.replace(/[^0-9.]/, "");

          if (newValue === "") {
            setValue(undefined);
            setDidUserInputZero(false);
          } else {
            const parsedValue = parseFloat(newValue);

            if (parsedValue === 0) {
              setValue(parsedValue);
              setDidUserInputZero(true);
            } else {
              setValue(parsedValue);
            }
          }
        }}
      />
    </div>
  );
};

interface PercentageInputProps {
  className: string;
  value?: number;
  setValue: (value?: number) => void;
  validator: boolean;
  onBlur?: () => void;
}
// input field that accepts only positive percentages (7% -> 0.07 in value)
// value is undefined if nothing has been typed in
export const PercentageInput = ({ className, value, setValue, validator, onBlur }: PercentageInputProps) => {
  const [didUserInputZero, setDidUserInputZero] = useState(value === 0);
  return (
    <div className="flex items-center">
      <input
        type="number"
        className={className + " flex-1 " + (validator ? "" : "border-red-500 bg-red-50")}
        onClick={(e) => e.currentTarget.select()}
        placeholder={PLACEHOLDER_TAX}
        value={value === 0 && !didUserInputZero ? "" : value}
        onBlur={onBlur}
        onKeyDown={(event) => {
          // allow for selection and deletion
          if (allowForSelectionAndDeletion(event)) {
            return;
          }

          // permit only positive decimals
          if (!/[0-9.]/.test(event.key)) {
            event.preventDefault();
          }
        }}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        onKeyUp={onBlur ? (e) => (e.key === "Enter" ? onBlur() : undefined) : undefined}
        onChange={(e) => {
          // permit only decimals, for button press
          if (e.target.value.replaceAll(/[0-9.]*/g, "").length !== 0) {
            console.log("PREVENT");
            e.preventDefault();
            return;
          }

          const newValue = e.target.value.replace(/[^0-9.]/, "");

          if (newValue === "") {
            setValue(undefined);
            setDidUserInputZero(false);
          } else {
            const parsedValue = parseFloat(newValue);

            if (parsedValue === 0) {
              setValue(parsedValue);
              setDidUserInputZero(true);
            } else {
              setValue(parsedValue);
            }
          }
        }}
      />
      <div className="pl-2 font-bold text-xl">%</div>
    </div>
  );
};

interface CurrencyInputProps {
  className: string;
  placeholder: string;

  value: string;
  setValue: (value: string, countryName?: string) => void;
  onBlur?: () => void;
}

// input field that accepts three letter denominations for currency
export const CurrencyInput = ({ className, placeholder, value, setValue, onBlur }: CurrencyInputProps) => {
  const options = Array.from(validCurrencies).map((currency) => ({
    value: currency,
    label: currency,
  }));

  const handleChange = (selectedOption: any) => {
    if (selectedOption) {
      setValue(selectedOption.value, currencyToCountry.get(selectedOption.value));
    } else {
      setValue("");
    }
  };

  return (
    <Select
      className={className}
      placeholder={placeholder}
      value={options.find((option) => option.value === value)}
      onChange={handleChange}
      options={options}
      isSearchable
      onBlur={onBlur}
    />
  );
};

// Add autoResize function
const autoResizeTextArea = (element: HTMLTextAreaElement) => {
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
};

interface StringInputProps {
  className?: string;
  value?: string;
  placeholder: string;
  setValue: (value: string) => void;
  validator: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  multiline?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  minHeight?: number;
}

export const StringInput = ({
  className,
  placeholder,
  value,
  setValue,
  validator,
  onBlur,
  disabled,
  multiline,
  onFocus,
  minHeight = 50,
}: StringInputProps) => {
  return multiline ? (
    <textarea
      disabled={disabled}
      className={className + " " + (validator ? "" : "border-red-500 bg-red-50")}
      placeholder={placeholder}
      value={value}
      onBlur={onBlur}
      onFocus={onFocus}
      style={{ resize: "none", overflow: "hidden", minHeight: minHeight }}
      onChange={(e) => {
        const newValue = e.target.value;
        setValue(newValue);
        autoResizeTextArea(e.target);
      }}
    />
  ) : (
    <input
      disabled={disabled}
      type="text"
      className={className + " " + (validator ? "" : "border-red-500 bg-red-50")}
      placeholder={placeholder}
      value={value}
      onBlur={onBlur}
      onFocus={onFocus}
      onChange={(e) => {
        const newValue = e.target.value;
        setValue(newValue);
      }}
    />
  );
};

interface DropdownInputProps {
  className?: string;
  choices: string[];
  displayText: string;
  value: string | undefined;
  setValue: (value: string | undefined) => void;
  onFirstLoad?: () => void;
}
/**
 * Helper input that allows the user to view a list of dropdown options, and select one.
 *
 * TODO: This should become a bit more generic, then it can be used and transferred over to special_input.tsx
 *
 */
export const DropdownInput = ({
  className,
  choices,
  displayText,
  value,
  setValue,
  onFirstLoad,
}: DropdownInputProps) => {
  const options = [{ value: "", label: "No Label" }, ...choices.map((choice) => ({ value: choice, label: choice }))];

  const handleChange = (selectedOption: any) => {
    setValue(selectedOption ? selectedOption.value : undefined);
  };

  return (
    <Select
      className={className}
      placeholder={displayText}
      value={options.find((option) => option.value === value)}
      onChange={handleChange}
      options={options}
      isSearchable
    />
  );
};

/**
 * Matches digits, decimal points, +, -, *, /, parentheses, whitespace
 * This disallows letters and other symbols.
 */
const ALLOWED_CHARS_REGEX = /^[0-9+\-*/().\s]*$/;
const PLAIN_NUMBER_REGEX = /^[0-9]*\.?[0-9]*$/;

/**
 * Checks if the key press is for navigation, text selection, or deletion.
 */
function isNavigationOrDeletionKey(event: React.KeyboardEvent<HTMLInputElement>): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.key === "Backspace" ||
    event.key === "ArrowLeft" ||
    event.key === "ArrowRight" ||
    event.key === "Delete"
  );
}

/**
 * Safely tries to parse a given arithmetic expression
 * and returns a number if valid, otherwise undefined.
 *
 * In production, consider using a safer math parser,
 * e.g. `mathjs` to avoid `new Function`.
 */
function tryParseExpression(expr: string): number | undefined {
  // If any disallowed characters, bail out early:
  if (!ALLOWED_CHARS_REGEX.test(expr)) {
    return undefined;
  }
  // Attempt to evaluate
  try {
    const result = new Function(`return (${expr});`)();
    if (typeof result === "number" && !isNaN(result)) {
      return result;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Props matching the existing WholeNumberInput so it can be a drop-in replacement
 */
export interface NumberInputWithCalculatorProps {
  className: string;
  value?: number;
  setValue: (value?: number) => void;
  validator?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  currency?: string;
}

/**
 * A drop-in replacement for the WholeNumberInput that also accepts
 * arithmetic expressions, parses them on the fly, and commits
 * the numeric value when Tab/Enter is pressed.
 */
export const NumberInputWithCalculator = ({
  className,
  value,
  setValue,
  validator = true,
  onBlur,
  disabled,
  currency,
}: NumberInputWithCalculatorProps) => {
  const [expression, setExpression] = useState<string>(value !== undefined ? String(value) : "");
  const [parsedValue, setParsedValue] = useState<number | undefined>(value);
  const currencySymbol = currency ? getCurrencySymbol(currency) : null;
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Create a ref for the input element
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expression.trim() === "") {
      setParsedValue(value); // Use the existing value if expression is empty

      if (isFirstRender && value !== undefined) {
        commitValue();
        setIsFirstRender(false);
      }
      return;
    }
    const possibleNumber = tryParseExpression(expression);
    if (possibleNumber !== parsedValue) {
      setParsedValue(possibleNumber);
    }
  }, [expression, value]); // Add value to dependencies

  useEffect(() => {
    if (PLAIN_NUMBER_REGEX.test(expression.trim())) {
      const asFloat = parseFloat(expression.trim());
      if (!isNaN(asFloat) && asFloat !== value) {
        setValue(asFloat);
      }
    }
  }, [expression, value]);

  const commitValue = () => {
    if (parsedValue !== undefined && parsedValue !== value) {
      setValue(parsedValue);
      setExpression(String(parsedValue));
    } else if (expression.trim() === "") {
      setExpression(value !== undefined ? String(value) : "");
    }
    onBlur?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      commitValue();
      return;
    }
    if (!ALLOWED_CHARS_REGEX.test(event.key) && !isNavigationOrDeletionKey(event)) {
      event.preventDefault();
    }
  };

  return (
    <div className="relative">
      {currencySymbol && (
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
          {currencySymbol}
        </span>
      )}
      <input
        ref={inputRef}
        type="text"
        className={
          className +
          " " +
          (!validator || (expression && parsedValue === undefined) ? "border-red-500 bg-red-50" : "") +
          (currencySymbol ? " pl-10" : "")
        }
        disabled={disabled}
        value={expression}
        onChange={(e) => {
          setExpression(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={commitValue}
      />
      {expression && parsedValue !== undefined && expression !== String(parsedValue) && (
        <div className="text-gray-500 text-sm mt-1">= {parsedValue}</div>
      )}
    </div>
  );
};
